/**
 * バージョンチェック機能
 * Firestoreからバージョン情報を取得し、アップデートが必要か判定
 */

import { firebaseConfigTickets as firebaseConfig } from './firebaseConfig_tickets.js';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, getDoc, collection } from 'firebase/firestore';
import config from './config';
import { getTranslation } from './i18n';

/**
 * 現在のアプリ言語を取得
 * @returns {string} 言語コード
 */
function getCurrentLang() {
  return (typeof localStorage !== 'undefined' && localStorage.getItem('appLanguage')) || 'en';
}

// Firebase初期化 (configから正しいデータベースIDを使用)
const app = initializeApp(firebaseConfig);
const db = getFirestore(app, config.firestoreDatabaseId);

// バージョン情報コレクション名
const VERSION_COLLECTION = 'platform_versions';
const PLATFORM_VERSIONS_DOC = 'platform_versions';

// プラットフォームタイプ
export const PLATFORM_TYPES = {
  WIN: 'WIN',
  WIN_STORE: 'WIN_STORE',
  LINUX: 'LINUX',
  LINUX_SNAP: 'LINUX_SNAP',
  LINUX_ITACH: 'LINUX_ITACH',
  ANDROID: 'ANDROID',
  ANDROID_STORE: 'ANDROID_STORE',
  IOS: 'IOS',
  IOS_STORE: 'IOS_STORE',
  MAC: 'MAC',
  MAC_STORE: 'MAC_STORE',
  WEB: 'WEB'
};

// 現在のアプリバージョン（環境変数から取得）
// 開発環境: REACT_APP_VERSION (parcelで設定)
// 本番環境: APP_VERSION (electron-builderで設定)
export const CURRENT_VERSION = process.env.REACT_APP_VERSION || process.env.APP_VERSION || '1.0.0';

/**
 * バージョン文字列をパース
 * @param {string} version - X.Y.Z形式のバージョン文字列
 * @returns {Object} パースされたバージョン情報
 */
export function parseVersion(version) {
  const parts = version.split('.').map(part => parseInt(part, 10) || 0);
  return {
    major: parts[0] || 0,
    minor: parts[1] || 0,
    patch: parts[2] || 0,
    raw: version
  };
}

/**
 * バージョン比較
 * @param {string} current - 現在のバージョン
 * @param {string} latest - 最新のバージョン
 * @returns {Object} 比較結果
 */
export function compareVersions(current, latest) {
  const currentVer = parseVersion(current);
  const latestVer = parseVersion(latest);

  // 数値として大小比較（major→minor→patchの優先順位で正しく判定）
  const currentNum = currentVer.major * 10000 + currentVer.minor * 100 + currentVer.patch;
  const latestNum  = latestVer.major  * 10000 + latestVer.minor  * 100 + latestVer.patch;

  const isMajorUpdate = latestVer.major > currentVer.major;
  const isMinorUpdate = !isMajorUpdate && latestVer.minor > currentVer.minor;
  const isPatchUpdate = !isMajorUpdate && !isMinorUpdate && latestVer.patch > currentVer.patch;

  const isUpdateRequired = isMajorUpdate || isMinorUpdate;
  const isUpdateAvailable = latestNum > currentNum;

  return {
    isUpdateRequired,
    isUpdateAvailable,
    isMajorUpdate,
    isMinorUpdate,
    isPatchUpdate,
    currentVersion: currentVer,
    latestVersion: latestVer
  };
}

/**
 * プラットフォームを自動検出
 * @returns {string} プラットフォームタイプ
 */
export function detectPlatform() {
  const buildPlatformId = process.env.REACT_APP_PLATFORM_ID;
  if (buildPlatformId) return buildPlatformId;

  const isElectron = navigator.userAgent.toLowerCase().includes('electron');
  if (isElectron) {
    if (process.platform === 'win32') return PLATFORM_TYPES.WIN;
    if (process.platform === 'darwin') return PLATFORM_TYPES.MAC;
    if (process.platform === 'linux') return PLATFORM_TYPES.LINUX;
  }
  return PLATFORM_TYPES.WEB;
}

/**
 * Firestoreからバージョン情報を取得
 * @param {string} platform - プラットフォームタイプ
 * @returns {Promise<Object|null>} バージョン情報
 */
export async function getVersionInfo(platform = null) {
  try {
    // 開発サーバー（port 3000）のみスキップ。ビルド版のローカルサーバー（port 8765等）はチェックする
    if (typeof window !== 'undefined' && window.location.hostname === 'localhost' && window.location.port === '3000') {
      console.log('[versionCheck] Skipping version check in development environment');
      return null;
    }
    
    const targetPlatform = platform || detectPlatform();
    console.log('[versionCheck] Fetching version for platform:', targetPlatform);

    // app_versions/{platformId} を直接読む
    const docRef = doc(db, VERSION_COLLECTION, targetPlatform);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      console.warn(`[versionCheck] Version document not found: app_versions/${targetPlatform}`);
      return null;
    }

    const platformData = docSnap.data();
    
    return {
      platform: targetPlatform,
      latestVersion: platformData.version || '1.0.0',
      updateUrl: platformData.updateUrl || '',
      releaseNotes: platformData.releaseNotes || '',
      forceUpdate: platformData.forceUpdate || false,
      minRequiredVersion: platformData.minRequiredVersion || '1.0.0',
      lastUpdated: platformData.lastUpdated || new Date().toISOString()
    };
  } catch (error) {
    // エラーをログに出力せず、静かに失敗させる（開発環境での権限エラーを防ぐ）
    console.warn('[versionCheck] Failed to fetch version info (development mode?)');
    return null;
  }
}

/**
 * バージョンチェックを実行
 * @param {string} currentVersion - 現在のバージョン
 * @param {string} platform - プラットフォームタイプ（省略時は自動検出）
 * @returns {Promise<Object>} チェック結果
 */
export async function checkVersionUpdate(currentVersion = CURRENT_VERSION, platform = null) {
  try {
    const versionInfo = await getVersionInfo(platform);
    
    if (!versionInfo) {
      return {
        success: false,
        error: 'Version info not found',
        updateRequired: false,
        updateAvailable: false
      };
    }
    
    const comparison = compareVersions(currentVersion, versionInfo.latestVersion);
    const minReqComparison = compareVersions(currentVersion, versionInfo.minRequiredVersion);
    const isBelowMinRequired = minReqComparison.isUpdateAvailable;

    console.log('[versionCheck] current:', currentVersion, 'latest:', versionInfo.latestVersion, 'minRequired:', versionInfo.minRequiredVersion);
    console.log('[versionCheck] isBelowMinRequired:', isBelowMinRequired, 'isUpdateRequired:', comparison.isUpdateRequired);

    return {
      success: true,
      platform: versionInfo.platform,
      currentVersion,
      latestVersion: versionInfo.latestVersion,
      updateUrl: versionInfo.updateUrl,
      releaseNotes: versionInfo.releaseNotes,
      forceUpdate: versionInfo.forceUpdate,
      minRequiredVersion: versionInfo.minRequiredVersion,
      lastUpdated: versionInfo.lastUpdated,
      ...comparison,
      shouldForceUpdate: versionInfo.forceUpdate || comparison.isUpdateRequired || isBelowMinRequired
    };
  } catch (error) {
    console.error('Version check failed:', error);
    return {
      success: false,
      error: error.message,
      updateRequired: false,
      updateAvailable: false
    };
  }
}

/**
 * アップデートページにリダイレクト
 * @param {string} updateUrl - アップデートURL
 */
export function redirectToUpdatePage(updateUrl) {
  if (!updateUrl) {
    console.error('No update URL provided');
    return;
  }
  
  // 強制アップデートの場合は現在のページを置き換える
  if (window.location.replace) {
    window.location.replace(updateUrl);
  } else {
    window.location.href = updateUrl;
  }
}

/**
 * レンダラー内HTMLダイアログを表示（Snap環境でのネイティブダイアログ豆腐対策）
 * @param {Object} opts - { title, message, detail, primaryBtn, secondaryBtn }
 * @returns {Promise<boolean>} - primaryボタンが押されたらtrue
 */
function showHtmlDialog({ title, message, detail, primaryBtn, secondaryBtn }) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';

    const dialog = document.createElement('div');
    dialog.style.cssText = 'background:#fff;border-radius:8px;padding:24px;max-width:420px;width:90%;box-shadow:0 4px 24px rgba(0,0,0,0.3);font-family:sans-serif;';

    const titleEl = document.createElement('h3');
    titleEl.textContent = title;
    titleEl.style.cssText = 'margin:0 0 12px;font-size:16px;color:#333;';

    const msgEl = document.createElement('p');
    msgEl.textContent = message;
    msgEl.style.cssText = 'margin:0 0 8px;font-size:14px;color:#555;';

    const detailEl = document.createElement('p');
    detailEl.textContent = detail;
    detailEl.style.cssText = 'margin:0 0 20px;font-size:13px;color:#777;white-space:pre-line;';

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;';

    const secondBtn = document.createElement('button');
    secondBtn.textContent = secondaryBtn;
    secondBtn.style.cssText = 'padding:8px 16px;border:1px solid #ccc;border-radius:4px;background:#fff;cursor:pointer;font-size:14px;';
    secondBtn.onclick = () => { overlay.remove(); resolve(false); };

    const primBtn = document.createElement('button');
    primBtn.textContent = primaryBtn;
    primBtn.style.cssText = 'padding:8px 16px;border:none;border-radius:4px;background:#1976d2;color:#fff;cursor:pointer;font-size:14px;';
    primBtn.onclick = () => { overlay.remove(); resolve(true); };

    btnRow.appendChild(secondBtn);
    btnRow.appendChild(primBtn);
    dialog.appendChild(titleEl);
    dialog.appendChild(msgEl);
    dialog.appendChild(detailEl);
    dialog.appendChild(btnRow);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  });
}

/**
 * バージョンチェックを実行し、必要に応じてアップデートを促す
 * @param {Object} options - オプション
 * @returns {Promise<Object>} チェック結果
 */
export async function performVersionCheck(options = {}) {
  const {
    currentVersion = CURRENT_VERSION,
    platform = null,
    showAlert = true,
    forceRedirect = false
  } = options;
  
  const result = await checkVersionUpdate(currentVersion, platform);
  
  if (!result.success) {
    return result;
  }

  // アップデートが必要な場合
  if (result.shouldForceUpdate) {
    console.log(`Update required: ${result.currentVersion.raw} -> ${result.latestVersion.raw}`);
    
    if (showAlert) {
      const lang = getCurrentLang();
      const confirmed = await showHtmlDialog({
        title: getTranslation(lang, 'versionChecker.forceUpdateMessage'),
        message: `${getTranslation(lang, 'versionChecker.updateAvailable')} (v${result.latestVersion.raw})`,
        detail: `${getTranslation(lang, 'versionChecker.releaseNotes')}: ${result.releaseNotes || '-'}`,
        primaryBtn: getTranslation(lang, 'versionChecker.updateNow'),
        secondaryBtn: getTranslation(lang, 'versionChecker.later'),
      });
      if (confirmed) {
        redirectToUpdatePage(result.updateUrl);
      }
    } else if (forceRedirect) {
      redirectToUpdatePage(result.updateUrl);
    }
  } else if (result.isUpdateAvailable) {
    console.log(`Update available: ${result.currentVersion.raw} -> ${result.latestVersion.raw}`);
    
    if (showAlert) {
      const lang = getCurrentLang();
      const confirmed = await showHtmlDialog({
        title: getTranslation(lang, 'versionChecker.updateAvailable'),
        message: `${getTranslation(lang, 'versionChecker.latestVersion')}: v${result.latestVersion.raw}`,
        detail: `${getTranslation(lang, 'versionChecker.releaseNotes')}: ${result.releaseNotes || '-'}`,
        primaryBtn: getTranslation(lang, 'versionChecker.updateNow'),
        secondaryBtn: getTranslation(lang, 'versionChecker.later'),
      });
      if (confirmed) {
        redirectToUpdatePage(result.updateUrl);
      }
    }
  } else {
    console.log(`Version is up to date: ${result.currentVersion.raw}`);
  }
  
  return result;
}

// デフォルトエクスポート
export default {
  PLATFORM_TYPES,
  CURRENT_VERSION,
  parseVersion,
  compareVersions,
  detectPlatform,
  getVersionInfo,
  checkVersionUpdate,
  redirectToUpdatePage,
  performVersionCheck
};